/**
 * ProfilePage — authenticated user self-service
 *
 * Tabs:
 *   1. Profile  — avatar, basic info, extended professional info, change password
 *   2. Certificates — user's earned certs, add from catalog, with optional image
 *   3. Job Settings — CV upload, job notifications toggle, job preference
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiService, API_BASE } from '../services/api';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Password strength meter
// ---------------------------------------------------------------------------
const getStrength = (pw) => {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 8)  score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/\d/.test(pw))   score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(pw)) score++;
  if (score <= 2) return { score, label: 'Weak',   color: 'bg-red-500' };
  if (score <= 4) return { score, label: 'Fair',   color: 'bg-yellow-500' };
  if (score <= 5) return { score, label: 'Good',   color: 'bg-blue-500' };
  return           { score, label: 'Strong', color: 'bg-emerald-500' };
};

const StrengthBar = ({ password }) => {
  const { score, label, color } = getStrength(password);
  if (!password) return null;
  const pct = Math.round((score / 6) * 100);
  return (
    <div className="mt-1.5 space-y-1">
      <div className="h-1.5 w-full rounded-full bg-slate-700">
        <div className={`h-1.5 rounded-full transition-all duration-300 ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <p className={`text-xs font-medium ${score <= 2 ? 'text-red-400' : score <= 4 ? 'text-yellow-400' : score <= 5 ? 'text-blue-400' : 'text-emerald-400'}`}>{label}</p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Stat card + skeleton
// ---------------------------------------------------------------------------
const StatCard = ({ icon, label, value, sub }) => (
  <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5 flex items-start gap-4">
    <div className="text-2xl">{icon}</div>
    <div className="min-w-0">
      <p className="text-2xl font-bold text-white leading-tight">{value}</p>
      <p className="text-sm font-medium text-slate-300">{label}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const StatSkeleton = () => (
  <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5 flex items-start gap-4 animate-pulse">
    <div className="w-8 h-8 rounded-lg bg-slate-700/60 flex-shrink-0" />
    <div className="min-w-0 flex-1 space-y-2 pt-1">
      <div className="h-6 w-10 rounded bg-slate-700/60" />
      <div className="h-3 w-24 rounded bg-slate-700/40" />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Section card wrapper
// ---------------------------------------------------------------------------
const SectionCard = ({ title, children }) => (
  <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl overflow-hidden">
    <div className="px-6 py-4 border-b border-slate-700/40">
      <h2 className="text-base font-semibold text-white">{title}</h2>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Input helper
// ---------------------------------------------------------------------------
const FormInput = ({ label, type = 'text', value, onChange, disabled, placeholder, maxLength, note, required }) => (
  <div>
    <label className="block text-xs font-medium text-slate-400 mb-1.5">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      maxLength={maxLength}
      required={required}
      className={`w-full rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-colors ${
        disabled
          ? 'bg-slate-900/30 border border-slate-700 text-slate-500 cursor-not-allowed'
          : 'bg-slate-900/60 border border-slate-600'
      }`}
    />
    {note && <p className="text-xs text-slate-600 mt-1">{note}</p>}
  </div>
);

// ---------------------------------------------------------------------------
// Completion bar
// ---------------------------------------------------------------------------
const CompletionBar = ({ profile }) => {
  const fields = [
    profile?.first_name, profile?.last_name, profile?.mobile_number,
    profile?.city, profile?.bio, profile?.current_title,
    profile?.avatar_url, profile?.cv_url,
    profile?.social_linkedin || profile?.social_github,
  ];
  const filled = fields.filter(Boolean).length;
  const pct = Math.round((filled / fields.length) * 100);
  return (
    <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-slate-300">Profile Completion</p>
        <span className={`text-sm font-bold ${pct >= 80 ? 'text-emerald-400' : pct >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>{pct}%</span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-700">
        <div
          className={`h-2 rounded-full transition-all duration-500 ${pct >= 80 ? 'bg-emerald-500' : pct >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {pct < 100 && <p className="text-xs text-slate-500 mt-1.5">Add city, bio, job title, CV, and social links to reach 100%</p>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Certificate date picker constants
// ---------------------------------------------------------------------------
const MONTHS = [
  { value: '01', label: 'Jan' }, { value: '02', label: 'Feb' }, { value: '03', label: 'Mar' },
  { value: '04', label: 'Apr' }, { value: '05', label: 'May' }, { value: '06', label: 'Jun' },
  { value: '07', label: 'Jul' }, { value: '08', label: 'Aug' }, { value: '09', label: 'Sep' },
  { value: '10', label: 'Oct' }, { value: '11', label: 'Nov' }, { value: '12', label: 'Dec' },
];
const YEARS = Array.from({ length: 2030 - 2010 + 1 }, (_, i) => String(2010 + i));

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const ProfilePage = () => {
  const { user, updateUser } = useAuth();

  // Active tab
  const [activeTab, setActiveTab] = useState('profile');

  // Profile state
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);

  // Basic edit form
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', mobile_number: '' });
  const [editDirty, setEditDirty] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // Extended profile form
  const [extForm, setExtForm] = useState({
    city: '', bio: '', current_title: '', company: '',
    social_linkedin: '', social_github: '', social_twitter: '',
    social_instagram: '', social_website: '',
  });
  const [extDirty, setExtDirty] = useState(false);
  const [savingExt, setSavingExt] = useState(false);

  // Change password form
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingPw, setSavingPw] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // Avatar upload
  const fileInputRef = useRef(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');

  // Certificates
  const [userCerts, setUserCerts] = useState([]);
  const [certCatalog, setCertCatalog] = useState([]);
  const [loadingCerts, setLoadingCerts] = useState(false);
  const [showAddCert, setShowAddCert] = useState(false);
  const [certForm, setCertForm] = useState({ catalog_id: '', credential_id: '', issued_date: '', expiry_date: '' });
  const [issuedMonth, setIssuedMonth] = useState('');
  const [issuedYear, setIssuedYear] = useState('');
  const [expiryMonth, setExpiryMonth] = useState('');
  const [expiryYear, setExpiryYear] = useState('');
  const [noExpiry, setNoExpiry] = useState(false);
  const [certImageFile, setCertImageFile] = useState(null);
  const [savingCert, setSavingCert] = useState(false);
  const certImgInputRef = useRef(null);

  // CV upload
  const cvInputRef = useRef(null);
  const [cvUploading, setCvUploading] = useState(false);
  const [cvError, setCvError] = useState('');

  // Job settings
  const [jobNotif, setJobNotif] = useState(false);
  const [jobPref, setJobPref] = useState('any');
  const [savingJobSettings, setSavingJobSettings] = useState(false);

  // Email preferences
  const [emailPrefs, setEmailPrefs] = useState({
    announcements: true,
    job_alerts:    true,
    batch_news:    true,
    promotions:    true,
    greetings:     true,
  });
  const [savingEmailPrefs, setSavingEmailPrefs] = useState(false);

  // ---------------------------------------------------------------------------
  // Fetch on mount
  // ---------------------------------------------------------------------------
  const fetchProfile = useCallback(async () => {
    try {
      const data = await apiService.getUserProfile();
      setProfile(data);
      setEditForm({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        mobile_number: data.mobile_number || '',
      });
      setExtForm({
        city: data.city || '',
        bio: data.bio || '',
        current_title: data.current_title || '',
        company: data.company || '',
        social_linkedin: data.social_linkedin || '',
        social_github: data.social_github || '',
        social_twitter: data.social_twitter || '',
        social_instagram: data.social_instagram || '',
        social_website: data.social_website || '',
      });
      setJobNotif(data.job_notifications || false);
      setJobPref(data.job_preference || 'any');
    } catch (err) {
      toast.error('Failed to load profile');
    } finally {
      setLoadingProfile(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiService.getUserStats();
      setStats(data);
    } catch {
      // non-critical
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const fetchCerts = useCallback(async () => {
    setLoadingCerts(true);
    try {
      const [certs, catalog] = await Promise.all([
        apiService.getUserCertificates(),
        apiService.getCertificateCatalog(),
      ]);
      setUserCerts(certs);
      setCertCatalog(catalog);
    } catch {
      toast.error('Failed to load certificates');
    } finally {
      setLoadingCerts(false);
    }
  }, []);

  const fetchEmailPrefs = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE}/api/user/email-preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setEmailPrefs(data.email_preferences || {});
      }
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    fetchProfile();
    fetchStats();
    fetchEmailPrefs();
  }, [fetchProfile, fetchStats, fetchEmailPrefs]);

  // Lazy-load certificates tab
  useEffect(() => {
    if (activeTab === 'certs') {
      fetchCerts();
    }
  }, [activeTab, fetchCerts]);

  // ---------------------------------------------------------------------------
  // Basic profile save
  // ---------------------------------------------------------------------------
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!editDirty) return;
    const payload = {};
    if (editForm.first_name !== profile.first_name) payload.first_name = editForm.first_name.trim();
    if (editForm.last_name  !== profile.last_name)  payload.last_name  = editForm.last_name.trim();
    if (editForm.mobile_number !== profile.mobile_number) payload.mobile_number = editForm.mobile_number.trim();
    if (Object.keys(payload).length === 0) { setEditDirty(false); return; }
    setSavingProfile(true);
    try {
      await apiService.updateUserProfile(payload);
      setProfile((prev) => ({ ...prev, ...payload }));
      setEditDirty(false);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCancelEdit = () => {
    setEditForm({ first_name: profile?.first_name || '', last_name: profile?.last_name || '', mobile_number: profile?.mobile_number || '' });
    setEditDirty(false);
  };

  // ---------------------------------------------------------------------------
  // Extended profile save
  // ---------------------------------------------------------------------------
  const handleSaveExtended = async (e) => {
    e.preventDefault();
    if (!extDirty) return;
    const payload = {};
    const fields = ['city', 'bio', 'current_title', 'company', 'social_linkedin', 'social_github', 'social_twitter', 'social_instagram', 'social_website'];
    for (const f of fields) {
      const cur = extForm[f]?.trim() || '';
      const orig = profile?.[f] || '';
      if (cur !== orig) payload[f] = cur || null;
    }
    if (Object.keys(payload).length === 0) { setExtDirty(false); return; }
    setSavingExt(true);
    try {
      await apiService.updateExtendedProfile(payload);
      setProfile((prev) => ({ ...prev, ...payload }));
      setExtDirty(false);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Failed to update profile');
    } finally {
      setSavingExt(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Change password
  // ---------------------------------------------------------------------------
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error('Passwords do not match'); return; }
    setSavingPw(true);
    try {
      const res = await apiService.changePassword({ current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success(res.message || 'Password changed. Please log in again.');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Failed to change password');
    } finally {
      setSavingPw(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Avatar
  // ---------------------------------------------------------------------------
  const handleAvatarClick = () => { if (!avatarUploading) fileInputRef.current?.click(); };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setAvatarError('Only JPEG, PNG, and WebP images are allowed.'); e.target.value = ''; return;
    }
    if (file.size > 2 * 1024 * 1024) { setAvatarError('File must be under 2 MB.'); e.target.value = ''; return; }
    setAvatarUploading(true); setAvatarError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_BASE}/api/user/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${apiService.getToken()}` },
        body: formData,
      });
      if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.detail || 'Upload failed'); }
      const data = await response.json();
      setProfile((prev) => ({ ...prev, avatar_url: data.avatar_url }));
      updateUser({ avatar_url: data.avatar_url });
      toast.success('Profile photo updated');
    } catch (err) {
      setAvatarError(err.message || 'Upload failed. Please try again.');
    } finally {
      setAvatarUploading(false); e.target.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    setAvatarError('');
    try {
      const response = await fetch(`${API_BASE}/api/user/avatar`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${apiService.getToken()}` },
      });
      if (!response.ok) throw new Error('Could not remove photo');
      setProfile((prev) => ({ ...prev, avatar_url: null }));
      updateUser({ avatar_url: null });
      toast.success('Profile photo removed');
    } catch { setAvatarError('Could not remove photo. Please try again.'); }
  };

  // ---------------------------------------------------------------------------
  // CV upload
  // ---------------------------------------------------------------------------
  const handleCVChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setCvError('File must be under 5 MB.'); e.target.value = ''; return; }
    setCvUploading(true); setCvError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_BASE}/api/user/cv`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${apiService.getToken()}` },
        body: formData,
      });
      if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.detail || 'Upload failed'); }
      const data = await response.json();
      setProfile((prev) => ({ ...prev, cv_url: data.cv_url }));
      toast.success('CV uploaded successfully');
    } catch (err) {
      setCvError(err.message || 'Upload failed. Please try a different file.');
    } finally {
      setCvUploading(false); e.target.value = '';
    }
  };

  const handleDeleteCV = async () => {
    if (!window.confirm('Remove your CV?')) return;
    setCvError('');
    try {
      await apiService.deleteCV();
      setProfile((prev) => ({ ...prev, cv_url: null }));
      toast.success('CV removed');
    } catch { setCvError('Could not remove CV. Please try again.'); }
  };

  // ---------------------------------------------------------------------------
  // Email preferences save
  // ---------------------------------------------------------------------------
  const handleSaveEmailPrefs = async (e) => {
    e.preventDefault();
    setSavingEmailPrefs(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE}/api/user/email-preferences`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email_preferences: emailPrefs }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Failed to save preferences'); return; }
      setEmailPrefs(data.email_preferences || emailPrefs);
      toast.success('Email preferences saved');
    } catch (err) {
      toast.error(err?.message || 'Failed to save preferences');
    } finally {
      setSavingEmailPrefs(false);
    }
  };

  // ---------------------------------------------------------------------------
  // CV view / download (authenticated — avoids exposing UUID file path)
  // ---------------------------------------------------------------------------
  const handleCVAction = async (action = 'download') => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/user/cv/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        alert('Action failed. Please try again.');
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      if (action === 'view') {
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = 'resume.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      alert('Action failed. Please try again.');
    }
  };

  // ---------------------------------------------------------------------------
  // Job settings save
  // ---------------------------------------------------------------------------
  const handleSaveJobSettings = async (e) => {
    e.preventDefault();
    setSavingJobSettings(true);
    try {
      await apiService.updateExtendedProfile({ job_notifications: jobNotif, job_preference: jobPref });
      setProfile((prev) => ({ ...prev, job_notifications: jobNotif, job_preference: jobPref }));
      toast.success('Job preferences saved');
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Failed to save preferences');
    } finally {
      setSavingJobSettings(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Add certificate
  // ---------------------------------------------------------------------------
  const handleAddCert = async (e) => {
    e.preventDefault();
    if (!certForm.catalog_id) { toast.error('Please select a certificate'); return; }
    setSavingCert(true);
    try {
      const formData = new FormData();
      formData.append('cert_data', JSON.stringify({
        catalog_id: certForm.catalog_id,
        credential_id: certForm.credential_id || null,
        issued_date: certForm.issued_date || null,
        expiry_date: certForm.expiry_date || null,
      }));
      if (certImageFile) formData.append('image', certImageFile);

      const response = await fetch(`${API_BASE}/api/user/certificates`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${apiService.getToken()}` },
        body: formData,
      });
      if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.detail || 'Failed to add certificate'); }
      const data = await response.json();
      toast.success(`${data.catalog_name} certificate added`);
      setShowAddCert(false);
      setCertForm({ catalog_id: '', credential_id: '', issued_date: '', expiry_date: '' });
      setIssuedMonth(''); setIssuedYear('');
      setExpiryMonth(''); setExpiryYear('');
      setNoExpiry(false);
      setCertImageFile(null);
      fetchCerts();
    } catch (err) {
      toast.error(err.message || 'Failed to add certificate');
    } finally {
      setSavingCert(false);
    }
  };

  const handleRemoveCert = async (certId, certName) => {
    if (!window.confirm(`Remove ${certName} from your profile?`)) return;
    try {
      await apiService.removeUserCertificate(certId);
      setUserCerts((prev) => prev.filter((c) => c.id !== certId));
      toast.success('Certificate removed');
    } catch (err) {
      toast.error(err?.detail || 'Failed to remove certificate');
    }
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------
  // Display phone with leading + if not already present (display-only, DB value unchanged)
  const formatPhone = (num) => !num ? '' : num.startsWith('+') ? num : `+${num}`;

  const initials = profile
    ? `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase()
    : (user?.first_name?.[0] || 'U').toUpperCase();

  const memberSince = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long' })
    : '—';

  if (loadingProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500" />
      </div>
    );
  }

  const TABS = [
    { id: 'profile', label: '👤 Profile' },
    { id: 'certs', label: '🏆 Certificates' },
    { id: 'jobs', label: '💼 Job Settings' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      <div className="max-w-3xl mx-auto px-4 py-10 space-y-6">

        {/* ── Profile header ─────────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 border border-slate-600/40 rounded-2xl p-6 flex flex-col sm:flex-row items-center sm:items-start gap-5">
          {/* Avatar */}
          <div className="flex-shrink-0 flex flex-col items-center gap-1.5">
            <div
              className="relative w-20 h-20 rounded-full cursor-pointer group"
              onClick={handleAvatarClick}
              title={profile?.avatar_url ? 'Change photo' : 'Upload photo'}
            >
              {profile?.avatar_url ? (
                <img src={`${API_BASE}${profile.avatar_url}`} alt="Profile" className="w-20 h-20 rounded-full object-cover border-2 border-slate-600" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                  <span className="text-2xl font-bold text-white">{initials}</span>
                </div>
              )}
              <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                {avatarUploading
                  ? <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <span className="text-white text-xs font-medium">{profile?.avatar_url ? 'Change' : 'Upload'}</span>
                }
              </div>
            </div>
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleAvatarChange} disabled={avatarUploading} />
            {avatarError && <p className="text-red-400 text-xs text-center max-w-[120px]">{avatarError}</p>}
            {profile?.avatar_url && !avatarUploading && (
              <button onClick={handleRemoveAvatar} className="text-xs text-slate-500 hover:text-red-400 transition-colors">Remove photo</button>
            )}
          </div>
          {/* Meta */}
          <div className="text-center sm:text-left flex-1">
            <h1 className="text-xl font-bold text-white">{profile?.first_name} {profile?.last_name}</h1>
            <p className="text-slate-400 text-sm mt-0.5">{profile?.email}</p>
            {profile?.current_title && <p className="text-orange-400 text-sm mt-0.5">{profile.current_title}{profile.company ? ` · ${profile.company}` : ''}</p>}
            {profile?.city && <p className="text-slate-500 text-xs mt-0.5">📍 {profile.city}</p>}
            <p className="text-slate-500 text-xs mt-1">Member since {memberSince}</p>
          </div>
        </div>

        {/* ── Completion bar ──────────────────────────────────────────── */}
        <CompletionBar profile={profile} />

        {/* ── Stats ──────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {loadingStats || !stats ? (
            <>
              <StatSkeleton />
              <StatSkeleton />
              <StatSkeleton />
              <StatSkeleton />
            </>
          ) : (
            <>
              <StatCard icon="🧪" label="Labs Attempted"   value={stats.total_labs} />
              <StatCard icon="✅" label="Labs Completed"   value={stats.completed_labs} />
              <StatCard icon="🎯" label="Score"            value={stats.total_score ?? 0} sub="points earned" />
              <StatCard icon="🎓" label="Courses Purchased" value={stats.courses_purchased_count ?? 0} />
            </>
          )}
        </div>

        {/* ── Tab Navigation ──────────────────────────────────────────── */}
        <div className="flex gap-2 border-b border-slate-700/50">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-slate-800/80 text-white border border-slate-700/50 border-b-slate-800 -mb-px'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── TAB: Profile ───────────────────────────────────────────── */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Basic info */}
            <SectionCard title="Basic Information">
              <form onSubmit={handleSaveProfile} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormInput label="First Name" value={editForm.first_name} onChange={(e) => { setEditForm((f) => ({ ...f, first_name: e.target.value })); setEditDirty(true); }} maxLength={50} required />
                  <FormInput label="Last Name" value={editForm.last_name} onChange={(e) => { setEditForm((f) => ({ ...f, last_name: e.target.value })); setEditDirty(true); }} maxLength={50} required />
                </div>
                <FormInput
                  label="Mobile Number"
                  type="tel"
                  value={formatPhone(editForm.mobile_number)}
                  onChange={(e) => {
                    // Strip leading + before storing — display only, never persisted with +
                    const raw = e.target.value.startsWith('+') ? e.target.value.slice(1) : e.target.value;
                    setEditForm((f) => ({ ...f, mobile_number: raw }));
                    setEditDirty(true);
                  }}
                  maxLength={16}
                />
                <FormInput label="Email" type="email" value={profile?.email || ''} disabled note="Email cannot be changed. Contact support if needed." />
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={handleCancelEdit} disabled={!editDirty || savingProfile}
                    className="text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed text-sm font-medium px-5 py-2.5 rounded-lg border border-slate-600 hover:border-slate-400 transition-colors">
                    Cancel
                  </button>
                  <button type="submit" disabled={!editDirty || savingProfile}
                    className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors">
                    {savingProfile ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </SectionCard>

            {/* Extended professional info */}
            <SectionCard title="Professional Information">
              <form onSubmit={handleSaveExtended} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormInput label="Job Title" placeholder="e.g. Linux System Administrator" value={extForm.current_title} onChange={(e) => { setExtForm((f) => ({ ...f, current_title: e.target.value })); setExtDirty(true); }} maxLength={100} />
                  <FormInput label="Company" placeholder="e.g. TechCorp India" value={extForm.company} onChange={(e) => { setExtForm((f) => ({ ...f, company: e.target.value })); setExtDirty(true); }} maxLength={100} />
                </div>
                <FormInput label="City" placeholder="e.g. Jaipur, Rajasthan" value={extForm.city} onChange={(e) => { setExtForm((f) => ({ ...f, city: e.target.value })); setExtDirty(true); }} maxLength={100} />
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Bio <span className="text-slate-600">({extForm.bio.length}/150)</span></label>
                  <textarea
                    value={extForm.bio}
                    onChange={(e) => { setExtForm((f) => ({ ...f, bio: e.target.value })); setExtDirty(true); }}
                    maxLength={150}
                    rows={2}
                    placeholder="Short bio about yourself…"
                    className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 resize-none"
                  />
                </div>
                <div className="pt-1">
                  <p className="text-xs font-medium text-slate-400 mb-2">Social Links</p>
                  <div className="space-y-3">
                    <FormInput label="LinkedIn" placeholder="https://linkedin.com/in/yourname" value={extForm.social_linkedin} onChange={(e) => { setExtForm((f) => ({ ...f, social_linkedin: e.target.value })); setExtDirty(true); }} />
                    <FormInput label="GitHub" placeholder="https://github.com/yourname" value={extForm.social_github} onChange={(e) => { setExtForm((f) => ({ ...f, social_github: e.target.value })); setExtDirty(true); }} />
                    <FormInput label="Twitter / X" placeholder="https://twitter.com/yourname" value={extForm.social_twitter} onChange={(e) => { setExtForm((f) => ({ ...f, social_twitter: e.target.value })); setExtDirty(true); }} />
                    <FormInput label="Website" placeholder="https://yoursite.com" value={extForm.social_website} onChange={(e) => { setExtForm((f) => ({ ...f, social_website: e.target.value })); setExtDirty(true); }} />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => { setExtForm({ city: profile?.city || '', bio: profile?.bio || '', current_title: profile?.current_title || '', company: profile?.company || '', social_linkedin: profile?.social_linkedin || '', social_github: profile?.social_github || '', social_twitter: profile?.social_twitter || '', social_instagram: profile?.social_instagram || '', social_website: profile?.social_website || '' }); setExtDirty(false); }} disabled={!extDirty || savingExt}
                    className="text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed text-sm font-medium px-5 py-2.5 rounded-lg border border-slate-600 hover:border-slate-400 transition-colors">
                    Cancel
                  </button>
                  <button type="submit" disabled={!extDirty || savingExt}
                    className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors">
                    {savingExt ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </SectionCard>

            {/* Change password */}
            <SectionCard title="Change Password">
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Current Password</label>
                  <div className="relative">
                    <input type={showCurrentPw ? 'text' : 'password'} value={pwForm.current_password} onChange={(e) => setPwForm((f) => ({ ...f, current_password: e.target.value }))} required autoComplete="current-password"
                      className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-3 py-2.5 pr-10 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-colors" />
                    <button type="button" onClick={() => setShowCurrentPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 text-xs" tabIndex={-1}>{showCurrentPw ? 'Hide' : 'Show'}</button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">New Password</label>
                  <div className="relative">
                    <input type={showNewPw ? 'text' : 'password'} value={pwForm.new_password} onChange={(e) => setPwForm((f) => ({ ...f, new_password: e.target.value }))} required minLength={8} autoComplete="new-password"
                      className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-3 py-2.5 pr-10 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-colors" />
                    <button type="button" onClick={() => setShowNewPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 text-xs" tabIndex={-1}>{showNewPw ? 'Hide' : 'Show'}</button>
                  </div>
                  <StrengthBar password={pwForm.new_password} />
                  <ul className="mt-2 space-y-0.5 text-xs text-slate-500">
                    <li className={pwForm.new_password.length >= 8 ? 'text-emerald-400' : ''}>• At least 8 characters</li>
                    <li className={/[A-Z]/.test(pwForm.new_password) ? 'text-emerald-400' : ''}>• One uppercase letter</li>
                    <li className={/[a-z]/.test(pwForm.new_password) ? 'text-emerald-400' : ''}>• One lowercase letter</li>
                    <li className={/\d/.test(pwForm.new_password) ? 'text-emerald-400' : ''}>• One digit</li>
                    <li className={/[!@#$%^&*(),.?":{}|<>]/.test(pwForm.new_password) ? 'text-emerald-400' : ''}>• One special character</li>
                  </ul>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Confirm New Password</label>
                  <input type="password" value={pwForm.confirm_password} onChange={(e) => setPwForm((f) => ({ ...f, confirm_password: e.target.value }))} required autoComplete="new-password"
                    className={`w-full bg-slate-900/60 border rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-colors ${pwForm.confirm_password && pwForm.confirm_password !== pwForm.new_password ? 'border-red-500' : 'border-slate-600 focus:border-emerald-500'}`} />
                  {pwForm.confirm_password && pwForm.confirm_password !== pwForm.new_password && (
                    <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
                  )}
                </div>
                <div className="flex justify-end pt-2">
                  <button type="submit" disabled={savingPw || !pwForm.current_password || !pwForm.new_password || pwForm.new_password !== pwForm.confirm_password}
                    className="bg-orange-600 hover:bg-orange-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors">
                    {savingPw ? 'Changing…' : 'Change Password'}
                  </button>
                </div>
              </form>
            </SectionCard>
          </div>
        )}

        {/* ── TAB: Certificates ───────────────────────────────────────── */}
        {activeTab === 'certs' && (
          <div className="space-y-6">
            <SectionCard title="My Certificates">
              {loadingCerts ? (
                <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" /></div>
              ) : (
                <>
                  {userCerts.length === 0 && !showAddCert && (
                    <p className="text-slate-500 text-sm text-center py-4">No certificates added yet. Showcase your achievements!</p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    {userCerts.map((cert) => (
                      <div key={cert.id} className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-4 flex items-start gap-3">
                        {cert.image_url ? (
                          <img src={`${API_BASE}${cert.image_url}`} alt={cert.catalog_name} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-14 h-14 rounded-lg flex-shrink-0 flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: cert.color || '#6366f1' }}>
                            {cert.catalog_name}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-white leading-tight">{cert.full_name}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{cert.category}</p>
                          {cert.credential_id && <p className="text-xs text-slate-500 mt-0.5">ID: {cert.credential_id}</p>}
                          {cert.issued_date && <p className="text-xs text-slate-500 mt-0.5">Issued: {cert.issued_date}{cert.expiry_date ? ` · Exp: ${cert.expiry_date}` : ''}</p>}
                        </div>
                        <button onClick={() => handleRemoveCert(cert.id, cert.catalog_name)} className="text-slate-600 hover:text-red-400 transition-colors text-xs flex-shrink-0">✕</button>
                      </div>
                    ))}
                  </div>

                  {!showAddCert ? (
                    <button onClick={() => setShowAddCert(true)} className="w-full py-3 border-2 border-dashed border-slate-600 hover:border-emerald-500 text-slate-400 hover:text-emerald-400 rounded-xl text-sm font-medium transition-colors">
                      + Add Certificate
                    </button>
                  ) : (
                    <form onSubmit={handleAddCert} className="bg-slate-900/40 border border-slate-700/40 rounded-xl p-5 space-y-4">
                      <h3 className="text-sm font-semibold text-white">Add Certificate</h3>
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1.5">Certificate *</label>
                        <select value={certForm.catalog_id} onChange={(e) => setCertForm((f) => ({ ...f, catalog_id: e.target.value }))} required
                          className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50">
                          <option value="">Select certificate…</option>
                          {certCatalog.map((c) => (
                            <option key={c.id} value={c.id}>{c.name} — {c.full_name}</option>
                          ))}
                        </select>
                      </div>
                      <FormInput label="Credential ID (optional)" placeholder="e.g. 210-010-582" value={certForm.credential_id} onChange={(e) => setCertForm((f) => ({ ...f, credential_id: e.target.value }))} maxLength={100} />
                      {/* Issued Date — month + year dropdowns */}
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1.5">Issued Date</label>
                        <div className="flex gap-2">
                          <select
                            value={issuedMonth}
                            onChange={(e) => {
                              setIssuedMonth(e.target.value);
                              if (issuedYear) setCertForm((f) => ({ ...f, issued_date: `${issuedYear}-${e.target.value}` }));
                            }}
                            className="flex-1 bg-slate-900/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                          >
                            <option value="">Month</option>
                            {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                          </select>
                          <select
                            value={issuedYear}
                            onChange={(e) => {
                              setIssuedYear(e.target.value);
                              if (issuedMonth) setCertForm((f) => ({ ...f, issued_date: `${e.target.value}-${issuedMonth}` }));
                            }}
                            className="flex-1 bg-slate-900/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                          >
                            <option value="">Year</option>
                            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                          </select>
                        </div>
                      </div>

                      {/* Expiry Date — month + year dropdowns + "No expiry" checkbox */}
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-xs font-medium text-slate-400">Expiry Date</label>
                          <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={noExpiry}
                              onChange={(e) => {
                                setNoExpiry(e.target.checked);
                                if (e.target.checked) setCertForm((f) => ({ ...f, expiry_date: '' }));
                              }}
                              className="rounded border-slate-600 accent-emerald-500"
                            />
                            No expiry
                          </label>
                        </div>
                        {!noExpiry && (
                          <div className="flex gap-2">
                            <select
                              value={expiryMonth}
                              onChange={(e) => {
                                setExpiryMonth(e.target.value);
                                if (expiryYear) setCertForm((f) => ({ ...f, expiry_date: `${expiryYear}-${e.target.value}` }));
                              }}
                              className="flex-1 bg-slate-900/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                            >
                              <option value="">Month</option>
                              {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                            <select
                              value={expiryYear}
                              onChange={(e) => {
                                setExpiryYear(e.target.value);
                                if (expiryMonth) setCertForm((f) => ({ ...f, expiry_date: `${e.target.value}-${expiryMonth}` }));
                              }}
                              className="flex-1 bg-slate-900/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                            >
                              <option value="">Year</option>
                              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                            </select>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1.5">Certificate Image (optional)</label>
                        <input ref={certImgInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) setCertImageFile(f); }} />
                        <button type="button" onClick={() => certImgInputRef.current?.click()}
                          className="w-full py-2.5 border border-slate-600 hover:border-slate-400 rounded-lg text-sm text-slate-400 hover:text-white transition-colors">
                          {certImageFile ? certImageFile.name : '📷 Upload certificate image'}
                        </button>
                      </div>
                      <div className="flex justify-end gap-3 pt-1">
                        <button type="button" onClick={() => { setShowAddCert(false); setCertForm({ catalog_id: '', credential_id: '', issued_date: '', expiry_date: '' }); setIssuedMonth(''); setIssuedYear(''); setExpiryMonth(''); setExpiryYear(''); setNoExpiry(false); setCertImageFile(null); }}
                          className="text-slate-400 hover:text-white text-sm font-medium px-4 py-2 rounded-lg border border-slate-600 transition-colors">
                          Cancel
                        </button>
                        <button type="submit" disabled={savingCert}
                          className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                          {savingCert ? 'Adding…' : 'Add Certificate'}
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </SectionCard>
          </div>
        )}

        {/* ── TAB: Job Settings ───────────────────────────────────────── */}
        {activeTab === 'jobs' && (
          <div className="space-y-6">
            {/* CV Upload */}
            <SectionCard title="CV / Resume">
              <div className="space-y-4">
                {profile?.cv_url ? (
                  <div className="flex items-center gap-4 bg-slate-900/40 border border-slate-700/40 rounded-xl p-4">
                    <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-red-400 text-lg">📄</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">CV uploaded</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <button onClick={() => handleCVAction('view')} className="text-xs px-2.5 py-1 rounded-md border border-slate-600 text-slate-300 hover:text-white hover:border-slate-400 transition-colors">View</button>
                        <button onClick={() => handleCVAction('download')} className="text-xs px-2.5 py-1 rounded-md bg-emerald-700 hover:bg-emerald-600 text-white transition-colors">Download</button>
                      </div>
                    </div>
                    <button onClick={handleDeleteCV} className="text-xs text-slate-500 hover:text-red-400 transition-colors">Remove</button>
                  </div>
                ) : (
                  <div className="text-center py-6 border-2 border-dashed border-slate-600 rounded-xl">
                    <p className="text-slate-400 text-sm mb-3">Upload your CV/Resume (PDF, max 5 MB)</p>
                    <button type="button" onClick={() => cvInputRef.current?.click()} disabled={cvUploading}
                      className="bg-emerald-700 hover:bg-emerald-600 disabled:bg-slate-700 text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors">
                      {cvUploading ? 'Uploading…' : '📤 Upload CV'}
                    </button>
                  </div>
                )}
                {cvError && <p className="text-red-400 text-xs">{cvError}</p>}
                <input ref={cvInputRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={handleCVChange} disabled={cvUploading} />
                {profile?.cv_url && (
                  <button type="button" onClick={() => cvInputRef.current?.click()} disabled={cvUploading}
                    className="text-sm text-slate-400 hover:text-white transition-colors">
                    Replace CV
                  </button>
                )}
              </div>
            </SectionCard>

            {/* Job Notifications */}
            <SectionCard title="Job Notifications">
              <form onSubmit={handleSaveJobSettings} className="space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-white">Receive job alerts</p>
                    <p className="text-xs text-slate-500 mt-0.5">Get notified when employers post roles matching your skills</p>
                  </div>
                  <button type="button" onClick={() => setJobNotif((v) => !v)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${jobNotif ? 'bg-emerald-600' : 'bg-slate-700'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${jobNotif ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>

                {jobNotif && (
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">Job Location Preference</label>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { value: 'india', label: '🇮🇳 India', desc: 'Onsite India jobs' },
                        { value: 'remote', label: '🌐 Remote', desc: 'Work from anywhere' },
                        { value: 'any', label: '✨ Any', desc: 'All opportunities' },
                      ].map((opt) => (
                        <button key={opt.value} type="button" onClick={() => setJobPref(opt.value)}
                          className={`p-3 rounded-xl border text-left transition-colors ${jobPref === opt.value ? 'border-emerald-500 bg-emerald-500/10 text-white' : 'border-slate-600 bg-slate-900/40 text-slate-400 hover:border-slate-400'}`}>
                          <p className="text-sm font-medium">{opt.label}</p>
                          <p className="text-xs mt-0.5 opacity-70">{opt.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-2">
                  <button type="submit" disabled={savingJobSettings}
                    className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors">
                    {savingJobSettings ? 'Saving…' : 'Save Preferences'}
                  </button>
                </div>
              </form>
            </SectionCard>

            {/* Email Preferences */}
            <SectionCard title="Email Preferences">
              <form onSubmit={handleSaveEmailPrefs} className="space-y-5">
                <p className="text-xs text-slate-500">
                  Choose which types of emails you'd like to receive from GRRAS Solutions.
                  You can unsubscribe from specific categories without losing others.
                </p>
                {[
                  { key: 'announcements', label: 'Platform Announcements',  desc: 'News about platform updates, new features, and maintenance notices' },
                  { key: 'batch_news',    label: 'Batch & Course Updates',  desc: 'Updates about your enrolled courses and new batch schedules' },
                  { key: 'promotions',    label: 'Offers & Promotions',     desc: 'Discount codes, special offers, and limited-time deals' },
                  { key: 'job_alerts',    label: 'Job Alerts',              desc: 'Career opportunities posted by employers on the platform' },
                  { key: 'greetings',     label: 'Greetings & Occasions',   desc: 'Festival wishes, birthdays, and celebratory messages' },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-white">{label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEmailPrefs((prev) => ({ ...prev, [key]: !prev[key] }))}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${emailPrefs[key] ? 'bg-emerald-600' : 'bg-slate-700'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${emailPrefs[key] ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                ))}
                <div className="flex justify-end pt-2">
                  <button type="submit" disabled={savingEmailPrefs}
                    className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors">
                    {savingEmailPrefs ? 'Saving…' : 'Save Email Preferences'}
                  </button>
                </div>
              </form>
            </SectionCard>
          </div>
        )}

      </div>
    </div>
  );
};

export default ProfilePage;
